import { useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  Download,
  MoreVertical,
  PlusSquare,
  Share,
  Smartphone,
  X,
} from "lucide-react";

const SNOOZE_KEY = "zaviramed_pwa_install_guide_snoozed_until";
const ONE_DAY = 24 * 60 * 60 * 1000;

function isStandaloneApp() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isMobileDevice() {
  const userAgent = navigator.userAgent || "";
  const touchDevice = navigator.maxTouchPoints > 0;
  const smallScreen = window.matchMedia?.("(max-width: 820px)").matches;

  return /Android|iPhone|iPad|iPod/i.test(userAgent) || (touchDevice && smallScreen);
}

function getDeviceType() {
  const userAgent = navigator.userAgent || "";
  const isiPad = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;

  if (/iPhone|iPad|iPod/i.test(userAgent) || isiPad) return "ios";
  if (/Android/i.test(userAgent)) return "android";
  return "mobile";
}

function getInstallSteps(deviceType, canInstall) {
  if (canInstall) {
    return [
      { icon: Download, text: "Tap Install app." },
      { icon: CheckCircle2, text: "Open Zaviramed from your home screen." },
    ];
  }

  if (deviceType === "ios") {
    return [
      { icon: Share, text: "Tap the Share button in Safari." },
      { icon: PlusSquare, text: "Choose Add to Home Screen." },
      { icon: CheckCircle2, text: "Tap Add." },
    ];
  }

  if (deviceType === "android") {
    return [
      { icon: MoreVertical, text: "Tap the Chrome menu." },
      { icon: PlusSquare, text: "Choose Add to Home screen." },
      { icon: CheckCircle2, text: "Tap Install or Add." },
    ];
  }

  return [
    { icon: MoreVertical, text: "Open your browser menu." },
    { icon: PlusSquare, text: "Choose Add to Home screen." },
    { icon: CheckCircle2, text: "Open Zaviramed like an app." },
  ];
}

export default function PwaInstallGuide() {
  const [visible, setVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [canInstall, setCanInstall] = useState(false);

  const deviceType = useMemo(() => {
    if (typeof window === "undefined") return "mobile";
    return getDeviceType();
  }, []);

  const steps = useMemo(
    () => getInstallSteps(deviceType, canInstall),
    [deviceType, canInstall]
  );

  useEffect(() => {
    if (!isMobileDevice() || isStandaloneApp()) return undefined;

    const snoozedUntil = Number(localStorage.getItem(SNOOZE_KEY) || 0);
    const showTimer = window.setTimeout(() => {
      if (Date.now() > snoozedUntil) setVisible(true);
    }, 1200);

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setCanInstall(true);
      setVisible(true);
    };

    const handleInstalled = () => setVisible(false);

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleInstalled);

    return () => {
      window.clearTimeout(showTimer);
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);

  const closeGuide = () => {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + ONE_DAY));
    setVisible(false);
  };

  const installApp = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;

    if (choice?.outcome === "accepted") {
      setVisible(false);
    }

    setDeferredPrompt(null);
    setCanInstall(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[9999] px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] md:hidden">
      <section className="mx-auto max-w-sm rounded-[26px] border border-emerald-100 bg-white/95 p-4 shadow-[0_18px_55px_rgba(7,94,84,0.18)] backdrop-blur-2xl">
        <div className="flex items-start gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-50">
            <img
              src="/zavira_nobg.png"
              alt="Zaviramed"
              className="h-9 w-9 object-contain"
            />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-black text-slate-950">
                  Add Zaviramed to your phone
                </p>
                <p className="mt-1 text-xs font-semibold leading-5 text-slate-500">
                  Open the doctor dashboard faster, like a normal app.
                </p>
              </div>

              <button
                type="button"
                onClick={closeGuide}
                aria-label="Close install guide"
                className="rounded-full p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 active:scale-95"
              >
                <X size={17} />
              </button>
            </div>

            <div className="mt-3 space-y-2">
              {steps.map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-2 text-xs font-bold text-slate-700">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-[#075E54]">
                    <Icon size={14} />
                  </span>
                  <span>{text}</span>
                </div>
              ))}
            </div>

            <div className="mt-4 flex gap-2">
              {canInstall && (
                <button
                  type="button"
                  onClick={installApp}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl bg-[#25D366] px-4 py-3 text-xs font-black text-[#05352e] transition hover:bg-[#20bf5b] active:scale-[0.98]"
                >
                  <Smartphone size={15} />
                  Install app
                </button>
              )}

              <button
                type="button"
                onClick={closeGuide}
                className="inline-flex flex-1 items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-3 text-xs font-black text-slate-700 transition hover:bg-slate-50 active:scale-[0.98]"
              >
                Later
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
